import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import serviceApi from '../../services/serviceApi';
import { formatDate, formatShortDateTime } from '../../utils/dateFormatter';
import ClientLayout from '../../components/ClientLayout';
import '../../styles/ClientLayout.css';
import { WHATSAPP_NUMBER } from '../../config/clientPortalConstants';
import '../../styles/VisitHistory.css';

/**
 * VisitHistory — client portal page showing completed visits with photos.
 * Accessed via /client/t/:token/visits (public route, token-based auth).
 * Uses ClientLayout wrapper. NO AuthContext, NO DesktopLayout.
 * See CLEANING_SERVICE_ARCHITECTURE.md → Client Portal Architecture Decisions.
 */

const VisitHistory = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visits, setVisits] = useState([]);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchVisits = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const res = await serviceApi.clientPortal.getVisits(token, page);
      const d = res.data || {};

      if (append) {
        setVisits(prev => [...prev, ...(d.visits || [])]);
      } else {
        setVisits(d.visits || []);
      }

      setClientName(d.clientName || '');
      setClientEmail(d.clientEmail || '');
      setPagination(d.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      console.error('Visit history error:', err);
      const status = err.response?.status;
      if (status === 401) {
        setError('invalid_token');
      } else {
        setError('network');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVisits(1);
  }, [fetchVisits]);

  const handleLoadMore = () => {
    if (loadingMore || pagination.page >= pagination.totalPages) return;
    fetchVisits(pagination.page + 1, true);
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="cd-layout-loading">
        <div className="cd-layout-loading__spinner" />
        <span className="cd-layout-loading__text">جاري التحميل...</span>
      </div>
    );
  }

  // ── Error: invalid token ──
  if (error === 'invalid_token') {
    return (
      <div className="cd-layout-error">
        <div className="cd-layout-error__icon"><i className="bi bi-shield-x" /></div>
        <div className="cd-layout-error__title">رابط غير صالح</div>
        <div className="cd-layout-error__sub">
          هذا الرابط لم يعد صالحاً أو غير صحيح.
          <br />
          تواصل مع فريق الدعم للحصول على رابط جديد.
        </div>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('مرحباً، أحتاج رابط جديد للوحة خدمات النظافة')}`}
          className="cd-layout-error__wa"
        >
          <i className="bi bi-whatsapp" />
          تواصل عبر واتساب
        </a>
      </div>
    );
  }

  // ── Error: network ──
  if (error === 'network') {
    return (
      <div className="cd-layout-error">
        <div className="cd-layout-error__icon"><i className="bi bi-wifi-off" /></div>
        <div className="cd-layout-error__title">تعذر الاتصال</div>
        <div className="cd-layout-error__sub">تحقق من اتصال الإنترنت وحاول مرة أخرى</div>
        <button onClick={() => fetchVisits(1)} className="cd-layout-error__retry">
          <i className="bi bi-arrow-clockwise" style={{ marginInlineStart: 6 }} />
          إعادة المحاولة
        </button>
      </div>
    );
  }

  const hasMore = pagination.page < pagination.totalPages;

  return (
    <ClientLayout activeTab="visits" clientName={clientName} clientEmail={clientEmail}>
      <div className="cd-visits">
        {/* Page Header */}
        <div className="cd-visits__header">
          <span className="cd-visits__title">سجل الزيارات</span>
          {pagination.total > 0 && (
            <span className="cd-visits__count">{pagination.total}</span>
          )}
        </div>

        {/* Empty State */}
        {visits.length === 0 ? (
          <div className="cd-visits__empty">
            <div className="cd-visits__empty-icon">
              <i className="bi bi-calendar-check" />
            </div>
            <div className="cd-visits__empty-title">لا توجد زيارات مكتملة بعد</div>
            <div className="cd-visits__empty-sub">
              ستظهر هنا الزيارات المكتملة مع صور إثبات العمل
            </div>
          </div>
        ) : (
          <>
            {/* Visit Cards */}
            {visits.map(visit => (
              <div key={visit._id} className="cd-visits__card">
                {/* Top row: date/location + status */}
                <div className="cd-visits__card-top">
                  <div className="cd-visits__card-info">
                    <span className="cd-visits__card-date">
                      {formatDate(visit.requestedDate)}
                    </span>
                    <span className="cd-visits__card-location">
                      <i className="bi bi-geo-alt" />
                      {visit.locationId?.name || '—'}
                    </span>
                  </div>
                  <span className="cd-visits__card-status">
                    <i className="bi bi-check-circle-fill" />
                    مكتمل
                  </span>
                </div>

                {/* Meta: ticket ID + completion date */}
                <div className="cd-visits__card-meta">
                  {visit.taskId?.ticketId && (
                    <span className="cd-visits__card-ticket">{visit.taskId.ticketId}</span>
                  )}
                  {visit.taskId?.completedAt && (
                    <span className="cd-visits__card-completed">
                      {formatShortDateTime(visit.taskId.completedAt)}
                    </span>
                  )}
                </div>

                {/* Completion Notes */}
                {visit.taskId?.completionNotes && (
                  <div className="cd-visits__card-notes">
                    {visit.taskId.completionNotes}
                  </div>
                )}

                {/* Photo Gallery */}
                {visit.taskId?.photos?.length > 0 && (
                  <div className="cd-visits__photos">
                    <span className="cd-visits__photos-label">
                      <i className="bi bi-camera" />
                      صور الزيارة
                    </span>
                    <div className="cd-visits__photos-scroll">
                      {visit.taskId.photos.map((photo, idx) => (
                        <div key={photo._id || idx} className="cd-visits__photo-thumb">
                          <img
                            src={photo.url}
                            alt={`صورة ${idx + 1}`}
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Load More */}
            {hasMore && (
              <div className="cd-visits__load-more">
                <button
                  className="cd-visits__load-more-btn"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <div className="cd-visits__loading-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
                      جاري التحميل...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-chevron-down" />
                      تحميل المزيد
                    </>
                  )}
                </button>
              </div>
            )}

            {/* End of list */}
            {!hasMore && visits.length > 0 && pagination.totalPages > 1 && (
              <div className="cd-visits__end">تم عرض جميع الزيارات</div>
            )}
          </>
        )}
      </div>
    </ClientLayout>
  );
};

export default VisitHistory;
